import { z } from 'zod';
import WorkOrderDetailsSidebar from '@/features/work-orders/components/WorkOrderDetailsSidebar.tsx';
import type { ComponentType } from 'react';
import type { SidebarStackItem } from '@/common/sidebar/sidebar-store.ts';
import SalesOrderNotesSidebar from '@/features/finance/sales-orders/components/SalesOrderNotesSidebar.tsx';
import SupportCaseDetailsSidebar from '@/features/support-cases/components/SupportCaseDetailsSidebar.tsx';
import SalesOrderDetailsSidebar from '@/features/finance/sales-orders/components/SalesOrderDetailsSidebar';

export type ExtraSidebarParams = {
  depth: number;
  hovered: boolean;
  collapsed: boolean;
};

type SidebarComponentParams<TParams extends z.ZodTypeAny> = z.infer<TParams> &
  ExtraSidebarParams;

export type SidebarDef<TId extends string, TParams extends z.ZodTypeAny> = {
  id: TId;
  params: TParams;
  component: ComponentType<SidebarComponentParams<TParams>>;
  width: number | ((params: z.infer<TParams>) => number);
};

function defineSidebar<
  const TId extends string,
  const TParams extends z.ZodTypeAny,
>(sidebar: SidebarDef<TId, TParams>) {
  return sidebar;
}

function defineSidebarRegistry<
  const TSidebars extends readonly SidebarDef<string, z.ZodTypeAny>[],
>(sidebars: TSidebars) {
  return Object.fromEntries(
    sidebars.map((sidebar) => [sidebar.id, sidebar]),
  ) as {
    [D in TSidebars[number] as D['id']]: D;
  };
}

const sidebarRegistry = defineSidebarRegistry([
  defineSidebar({
    id: 'workOrderDetails',
    params: z.object({
      workOrderId: z.string(),
      workOrderExternalId: z.string(),
    }),
    component: WorkOrderDetailsSidebar,
    width: 550,
  }),
  defineSidebar({
    id: 'salesOrderDetail',
    params: z.object({
      salesOrderId: z.string(),
      salesOrderExternalId: z.string(),
      defaultTab: z.enum(['notes', 'work-orders']),
    }),
    component: SalesOrderDetailsSidebar,
    width: 360,
  }),
  defineSidebar({
    id: 'salesOrderNotes',
    params: z.object({
      salesOrderId: z.string(),
      salesOrderExternalId: z.string(),
    }),
    component: SalesOrderNotesSidebar,
    width: 360,
  }),
  defineSidebar({
    id: 'supportCaseDetails',
    params: z.object({
      supportCaseId: z.string(),
      supportCaseExternalId: z.string(),
    }),
    component: SupportCaseDetailsSidebar,
    width: 550,
  }),
]);

type SidebarRegistry = typeof sidebarRegistry;

export type SidebarId = keyof SidebarRegistry;
export type SidebarParams<TId extends SidebarId> = z.infer<
  SidebarRegistry[TId]['params']
>;

export function getSidebarStackItem<TId extends SidebarId>(
  id: TId,
  params: SidebarParams<TId>,
): SidebarStackItem | null {
  const sidebar = sidebarRegistry[id];

  const parsedParams = sidebar.params.parse(params) as SidebarParams<TId>;

  const width =
    typeof sidebar.width === 'number'
      ? sidebar.width
      : (sidebar.width as (params: SidebarParams<TId>) => number)(parsedParams);

  return {
    key: `${sidebar.id}_${JSON.stringify(parsedParams)}`,
    id: sidebar.id,
    width,
    params: parsedParams,
  };
}

export function renderSidebar(
  id: string,
  depth: number,
  hovered: boolean,
  collapsed: boolean,
  params: Record<string, unknown>,
) {
  const sidebar = sidebarRegistry[id as SidebarId];

  const parsedParams = sidebar.params.parse(params);

  const sidebarParams: SidebarComponentParams<z.ZodTypeAny> = {
    ...(parsedParams as object),
    depth,
    hovered,
    collapsed,
  };

  const Component = sidebar.component;

  // @ts-expect-error issues w/ params and generic component type
  return <Component {...sidebarParams} />;
}
